<script setup lang="ts">
import { computed, ref } from 'vue'
import type { MediaAsset } from '../../api/types'
import { durationLabel } from '../../lib/media'
import Icon from '../ui/Icon.vue'

/**
 * Square thumbnail of a library file: the ≤ 240 px preview (video: poster + play mark + length).
 * The picture is shown whole (contain) on a soft background — never cropped, never stretched.
 */
const props = withDefaults(
  defineProps<{ asset: Pick<MediaAsset, 'kind' | 'title' | 'url' | 'preview_url' | 'duration_ms'>; size?: number; src?: string | null }>(),
  { size: 96, src: null },
)
const broken = ref(false)
const img = computed(() => props.src ?? props.asset.preview_url ?? (props.asset.kind === 'image' ? props.asset.url : null))
const length = computed(() => (props.asset.kind === 'video' ? durationLabel(props.asset.duration_ms) : null))
</script>

<template>
  <span class="mt" :style="{ width: `${size}px`, height: `${size}px` }">
    <img v-if="img && !broken" :src="img" :alt="asset.title || (asset.kind === 'video' ? 'วิดีโอ' : 'รูปภาพ')" loading="lazy" @error="broken = true" />
    <span v-else class="mt-none"><Icon :name="asset.kind === 'video' ? 'play' : 'image'" /></span>
    <span v-if="asset.kind === 'video'" class="mt-vid" aria-hidden="true">
      <Icon name="play" /><span v-if="length && size >= 72" class="mono">{{ length }}</span>
    </span>
  </span>
</template>

<style scoped>
.mt {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  border-radius: 8px;
  overflow: hidden;
  background: #eef2f0;
}
.mt img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.mt-none {
  color: var(--mut);
}
.mt-vid {
  position: absolute;
  left: 4px;
  bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  padding: 0 6px 0 4px;
  border-radius: 6px;
  background: rgba(12, 18, 15, 0.72);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}
.mt-vid :deep(.ic) {
  width: 12px;
  height: 12px;
  fill: currentColor;
  stroke: none;
}
</style>
